import React from "react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { Check } from "@dreamhub/designsystem/icons";
import IconContainer, { IconColor } from "@/components/ui/icon-container";

export type DropdownMenuListItemVariant = "default" | "destructive";

interface DropdownMenuListItemProps {
  option: string;
  onSelect: () => void;
  customIcon?: string | JSX.Element;
  customText?: string;
  renderIcon?: (option: string, iconColor?: IconColor) => React.ReactNode;
  text: string;
  variant?: DropdownMenuListItemVariant;
  disabled?: boolean;
  "data-test"?: string;
  disabledTooltipText?: string;
  selected?: boolean;
  secondaryText?: string;
}

const getVariantClasses = (variant: DropdownMenuListItemVariant) => {
  switch (variant) {
    case "destructive":
      return {
        item: "hover:!bg-surface-destructive-hover active:!bg-surface-destructive-pressed",
        text: "text-negative",
        icon: "negative" as const
      };
    case "default":
    default:
      return {
        item: "hover:!bg-surface-hover-soft active:!bg-surface-pressed",
        text: "",
        icon: "secondary" as const
      };
  }
};

const DropdownMenuListItem: React.FC<DropdownMenuListItemProps> = ({
  option,
  onSelect,
  customIcon,
  customText,
  renderIcon,
  text,
  variant = "default",
  disabled = false,
  "data-test": dataTest,
  disabledTooltipText,
  selected = false,
  secondaryText
}) => {
  const variantClasses = getVariantClasses(variant);
  const finalIconColor = disabled ? "tertiary" : variantClasses.icon;

  const MenuItem = (
    <DropdownMenuItem
      key={option}
      data-test={dataTest || option}
      className={cn(
        "flex gap-2",
        variantClasses.item,
        selected && "justify-between pointer-events-none",
        disabled && disabledTooltipText && "data-[disabled]:!opacity-100"
      )}
      onSelect={selected ? undefined : onSelect}
      disabled={disabled}
    >
      <div className="flex justify-between items-center w-full gap-4">
        <div className="flex gap-2 items-center">
          {customIcon
            ? customIcon
            : renderIcon && renderIcon(option, finalIconColor)}
          <Text
            as="div"
            variant="body"
            className={cn(
              variantClasses.text,
              selected && "font-semibold",
              disabled && "!text-disabled"
            )}
          >
            {customText ? customText : text}
          </Text>
        </div>
        <Text as="div" variant="smBodyTertiary">
          {secondaryText}
        </Text>
      </div>
      {selected && (
        <IconContainer size="5">
          <Check />
        </IconContainer>
      )}
    </DropdownMenuItem>
  );

  if (disabled && disabledTooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-full">{MenuItem}</div>
        </TooltipTrigger>
        <TooltipContent side="left" sideOffset={10} className="w-56">
          {disabledTooltipText}
        </TooltipContent>
      </Tooltip>
    );
  }

  return MenuItem;
};

export default DropdownMenuListItem;
